import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Transition } from 'react-transition-group';
import { Modal, Button, Text, Icon, H5 } from '../../../../../components';
import { ShareModalSelect } from '../../molecules/ShareModalSelect';
import { ShareModalHeader } from '../../molecules/ShareModalHeader';
import { ParticipantListItem } from '../../molecules/ParticipantListItem/ParticipantListItem';
import { useDeleteShareParticipant } from '../../../hooks/useDeleteShareParticipant';
import { userShape } from '../../../../../propsShape';

const rolesSchemaMap = {
  hris: 'hr',
  accounting: 'accountant',
  crm: 'sales',
  product: 'product'
};

const StyledButton = styled(Button)`
  margin-bottom: auto;
  border-radius: 20px;
  height: 40px;
`;

const SearchFormWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px 16px 0 16px;
  gap: 10px;
`;

const UsersListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  row-gap: 8px;
  padding: 0 16px 16px 16px;
  overflow: auto;
`;

const PatricipantsLabel = styled(H5)``;

const NoParticipantsLabel = styled(Text)`
  padding: 14px 8px;
  text-align: center;
`;

const TransitionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: max-height 200ms linear;
  overflow: hidden;
  max-height: ${({ $state, $height }) =>
    $state === 'entered' || $state === 'entering' ? `${$height}px` : 0};
`;

const AnimatedIcon = styled(Icon)`
  transition: transform 200ms linear;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(0deg)' : 'rotate(-90deg)')};
  cursor: pointer;
`;

const SupperParticipantsToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 4px;
  margin-bottom: 2px;
`;

const getEntitledUsers = (users, schema) =>
  users.filter((user) => {
    if (
      user.memberships[0].entitlements.some(
        (entitlement) => entitlement.role.name.toLowerCase() === 'admin'
      )
    )
      return true;

    if (
      user.memberships[0].entitlements.some(
        (entitlement) =>
          entitlement.role.name.toLowerCase() === rolesSchemaMap[schema?.toLowerCase()]
      )
    )
      return true;

    return false;
  });

const ShareModal = ({
  schema,
  label,
  onCreateShare,
  users,
  organizationParticipants,
  supperParticipants,
  shareableId,
  title,
  buttonLabel,
  shareableEntity,
  currentUser,
  type = 'question',
  filterUsers = false
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSupperOpen, setIsSupperOpen] = useState(false);
  const supperRef = useRef(null);
  const [supperHeight, setSupperHeight] = useState(0);
  const deleteShareParticipant = useDeleteShareParticipant();

  const eligibleApprovers = useMemo(() => getEntitledUsers(users, schema), [users]);

  useLayoutEffect(() => {
    if (supperRef.current) {
      requestAnimationFrame(() => {
        const height = supperRef.current.scrollHeight;
        setSupperHeight(height);
      });
    }
  }, [isSupperOpen, supperParticipants]);

  const handleSubmit = (close) => {
    onCreateShare(selectedItems, shareableId, type);
    close();
  };

  const handleDeleteParticipant = async (userId) => {
    const shareParticipant = shareableEntity?.shares?.[0]?.participants?.find(
      (p) => p.userId === userId
    );

    await deleteShareParticipant(shareParticipant.id, type, shareableId);
  };

  return (
    <Modal size='md' closeOnBackdropClick>
      {(close) => (
        <>
          <ShareModalHeader title={title} onClose={close} />
          <SearchFormWrapper>
            <ShareModalSelect
              users={filterUsers ? eligibleApprovers : users}
              selectedItems={selectedItems}
              participants={[...organizationParticipants, ...supperParticipants]}
              setSelectedItems={setSelectedItems}
              label={label}
            />
            <StyledButton secondary onClick={() => handleSubmit(close)}>
              {buttonLabel}
            </StyledButton>
          </SearchFormWrapper>
          <UsersListContainer>
            {organizationParticipants?.length > 0 && (
              <>
                <PatricipantsLabel>Members in this {type}</PatricipantsLabel>
                {organizationParticipants.map((item) => (
                  <ParticipantListItem
                    key={item?.id}
                    currentUserId={currentUser?.id}
                    item={item}
                    onDelete={handleDeleteParticipant}
                  />
                ))}
              </>
            )}
            {supperParticipants.length > 0 && (
              <>
                <SupperParticipantsToggleContainer>
                  <PatricipantsLabel>Supper team</PatricipantsLabel>
                  <AnimatedIcon
                    size={16}
                    name='chevron-down'
                    isOpen={isSupperOpen}
                    onClick={() => setIsSupperOpen(!isSupperOpen)}
                  />
                </SupperParticipantsToggleContainer>

                <Transition in={isSupperOpen} timeout={200} mountOnEnter unmountOnExit>
                  {(state) => (
                    <TransitionContainer ref={supperRef} $state={state} $height={supperHeight}>
                      {supperParticipants.map((item) => (
                        <ParticipantListItem
                          key={item?.id}
                          currentUserId={currentUser?.id}
                          item={item}
                          onDelete={handleDeleteParticipant}
                        />
                      ))}
                    </TransitionContainer>
                  )}
                </Transition>
              </>
            )}
            {!organizationParticipants?.length > 0 && !supperParticipants?.length > 0 && (
              <NoParticipantsLabel $bold $color='var(--content-title)'>
                No participants
              </NoParticipantsLabel>
            )}
          </UsersListContainer>
        </>
      )}
    </Modal>
  );
};

const user = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  picture: PropTypes.string
});

ShareModal.propTypes = {
  schema: PropTypes.string,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onCreateShare: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(user).isRequired,
  organizationParticipants: PropTypes.arrayOf(user).isRequired,
  supperParticipants: PropTypes.arrayOf(user).isRequired,
  shareableId: PropTypes.string.isRequired,
  shareableEntity: PropTypes.shape({
    shares: PropTypes.arrayOf(
      PropTypes.shape({
        participants: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            userId: PropTypes.string.isRequired
          })
        )
      })
    )
  }),
  type: PropTypes.string,
  filterUsers: PropTypes.bool,
  buttonLabel: PropTypes.string.isRequired,
  currentUser: PropTypes.shape(userShape)
};

export { ShareModal };
